import { useEffect, useState } from 'react';
import { toNumber } from 'lodash';
import { t } from 'i18next';
import { toast } from 'react-toastify';

import { ISubmissionTableFilters } from '@views/pages/DataAnalysis/components/Submissions/hooks/useSubmissions.ts';
import { SubmissionColumnNames } from '@views/pages/DataAnalysis/components/Submissions/components';
import { useSubmissionDownloadQuery } from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';

interface IUseSubmissionTableProps {
  filters: ISubmissionTableFilters;
  setFilters: (filters: ISubmissionTableFilters) => void;
}

export const useSubmissionsTable = ({
  filters,
  setFilters,
}: IUseSubmissionTableProps) => {
  const { sort_by, sort_order } = filters;
  const [downloadId, setDownloadId] = useState<string>('');

  const { data, isPending, mutateAsync } =
    useSubmissionDownloadQuery(downloadId);

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);

      const a = document.createElement('a');
      a.href = url;
      a.download = `submission-${downloadId}`;

      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
          setDownloadId('');
        }, 150);
      }

      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  const handleDownload = async (submission_id: string) => {
    toast.info(t('submissions.toast.loading'));
    setDownloadId(submission_id);

    try {
      await mutateAsync();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleChangeOrder = (colName: SubmissionColumnNames) => {
    setFilters({
      ...filters,
      sort_by: colName,
      sort_order: filters.sort_order === 'asc' ? 'desc' : 'asc',
    });
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setFilters({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, limit: toNumber(event.target.value), page: 1 });
  };

  return {
    sort_by,
    sort_order,
    onPageIndexChange,
    onPageSizeChange,
    handleChangeOrder,
    handleDownload,
    isDownloading: isPending,
  };
};
